import React, { useEffect, useMemo, useState } from 'react';
import {
  RogItemStatusValue,
  ROG_STATUS_COLOR,
  ROG_STATUS_LABEL,
  ROG_STATUS_VALUES,
} from './rogStatusUtils';

interface GtRogStatusEditorProps {
  positionKey: string;
  currentStatuses: RogItemStatusValue[];
  onApply: (positionKey: string, statuses: RogItemStatusValue[]) => void;
}

const setsEqual = (a: Set<string>, b: Set<string>): boolean => {
  if (a.size !== b.size) return false;
  for (const v of a) if (!b.has(v)) return false;
  return true;
};

export const GtRogStatusEditor: React.FC<GtRogStatusEditorProps> = ({
  positionKey,
  currentStatuses,
  onApply,
}) => {
  const [draft, setDraft] = useState<Set<RogItemStatusValue>>(new Set(currentStatuses));

  useEffect(() => {
    setDraft(new Set(currentStatuses));
  }, [positionKey, currentStatuses]);

  const dirty = useMemo(
    () => !setsEqual(draft as Set<string>, new Set(currentStatuses) as Set<string>),
    [draft, currentStatuses]
  );

  const toggleStatus = (status: RogItemStatusValue) => {
    setDraft((prev) => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      return next;
    });
  };

  const handleApply = () => {
    if (!dirty) return;
    const ordered = ROG_STATUS_VALUES.filter((s) => draft.has(s));
    onApply(positionKey, ordered);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <h4 className="text-lg font-semibold text-gray-900 mb-3">ROG Position Status (GT)</h4>

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <span className="text-sm font-medium text-gray-600">Current:</span>
        {currentStatuses.length === 0 ? (
          <span className="text-sm text-gray-400">Not set</span>
        ) : (
          currentStatuses.map((status) => (
            <span
              key={status}
              className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md text-xs font-medium"
              style={{
                backgroundColor: `${ROG_STATUS_COLOR[status]}22`,
                color: ROG_STATUS_COLOR[status],
              }}
            >
              <span
                className="inline-block w-2 h-2 rounded-full"
                style={{ backgroundColor: ROG_STATUS_COLOR[status] }}
              />
              {ROG_STATUS_LABEL[status]}
            </span>
          ))
        )}
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <span className="text-sm font-medium text-gray-600">New:</span>
        {ROG_STATUS_VALUES.map((status) => {
          const active = draft.has(status);
          const color = ROG_STATUS_COLOR[status];
          return (
            <button
              key={status}
              type="button"
              onClick={() => toggleStatus(status)}
              className="px-3 py-1 rounded-md text-sm font-medium border transition-colors"
              style={
                active
                  ? { backgroundColor: color, borderColor: color, color: '#fff' }
                  : {
                      backgroundColor: '#fff',
                      borderColor: '#d1d5db',
                      color: '#374151',
                    }
              }
            >
              {ROG_STATUS_LABEL[status]}
            </button>
          );
        })}
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleApply}
          disabled={!dirty}
          className="px-4 py-1.5 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Apply
        </button>
      </div>
    </div>
  );
};
